import type { v2 } from '@deque/billing-service-client';
import {
  Link as CauldronLink,
  ColumnHeader,
  ColumnLeft,
  ColumnRight,
  Loader,
  TwoColumnPanel
} from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Route, Switch, useLocation } from 'react-router-dom';
import ContentToast from '~/common/components/ContentToast';
import {
  ProductSlugs,
  type SupportedIntegrationProductSlugs
} from '~/common/constants';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import commonStyles from '~/common/utils/common.css';
import AxeAssistantTeamsConfiguration from './AxeAssistantTeamsConfiguration';
import AxeDeveloperHubConfiguration from './AxeDeveloperHubConfiguration';
import AxeDevToolsMobileConfiguration from './AxeDevToolsMobileConfiguration';
import AxeDevtoolsProConfiguration from './AxeDevtoolsProConfiguration';
import AxeMonitorConfiguration from './AxeMonitorConfiguration';
import styles from './Configuration.css';
import GlobalConfiguration from './GlobalConfiguration';
import CustomSettings from './integrations/custom/CustomSettings';
import IntegrationConfigurations from './integrations/IntegrationConfigurations';
import JiraDirectSettings from './integrations/jira-direct/JiraDirectSettings';
import JiraOnPremSettings from './integrations/jira-on-prem/JiraOnPremSettings';

export interface ConfigurationProps {
  narrow: boolean;
  loading: boolean;
  error: Error | null;
  canAccessIntegrationConfiguration: boolean;
  enabledIntegrationProductSlugs: SupportedIntegrationProductSlugs[];
  getProductBySlug: (slug: ProductSlugs) => v2.Product | undefined;
  hasAccessToProduct: (slug: ProductSlugs) => boolean;
  showNoActiveSubscriptionMessage: boolean;
  activeEnterprise: v2.Enterprise | null;
  isEnterpriseAdmin: boolean;
}

const Configuration: React.FC<ConfigurationProps> = ({
  narrow,
  loading,
  error,
  canAccessIntegrationConfiguration,
  enabledIntegrationProductSlugs,
  getProductBySlug,
  hasAccessToProduct,
  showNoActiveSubscriptionMessage,
  activeEnterprise,
  isEnterpriseAdmin
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const loaderRef = useRef<HTMLDivElement>(null);

  const shouldShowAxeMonitorConfigPage = useFeatureFlagState(
    'axe_monitor_config_page'
  );
  const shouldShowMobileRuleConfig = useFeatureFlagState(
    'axe_devhub_mobile_rule_config_v1'
  );

  const paths = useMemo(() => {
    const { pathname } = location;
    return {
      globalIsActive: pathname === '/configuration',
      axeDevToolsProIsActive:
        pathname === `/configuration/${ProductSlugs.axeDevToolsExtension}`,
      axeDeveloperHubIsActive:
        pathname === `/configuration/${ProductSlugs.axeDevToolsWatcher}`,
      axeDevToolsMobileIsActive:
        pathname === `/configuration/${ProductSlugs.axeDevToolsMobile}`,
      integrationsIsActive: pathname === `/configuration/integrations`,
      axeAssistantTeamsIsActive:
        pathname === `/configuration/${ProductSlugs.axeAssistantTeams}`,
      axeMonitorIsActive:
        pathname === `/configuration/${ProductSlugs.axeMonitor}`
    };
  }, [location]);

  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  if (error) {
    return (
      <ContentToast show={true} type="caution">
        {t('Failed to load configuration.')}
      </ContentToast>
    );
  }

  if (loading) {
    return (
      <Loader
        label={t('Loading configuration...')}
        tabIndex={-1}
        ref={loaderRef}
      />
    );
  }

  const axeDevToolsPro =
    hasAccessToProduct(ProductSlugs.axeDevToolsExtension) &&
    getProductBySlug(ProductSlugs.axeDevToolsExtension);
  const axeDeveloperHub =
    hasAccessToProduct(ProductSlugs.axeDevToolsWatcher) &&
    getProductBySlug(ProductSlugs.axeDevToolsWatcher);
  // Mobile rule configuration is org-level only, so the section is limited to
  // enterprise members (PRD: no individual-user mobile configuration).
  const axeDevToolsMobile =
    shouldShowMobileRuleConfig &&
    !!activeEnterprise &&
    hasAccessToProduct(ProductSlugs.axeDevToolsMobile) &&
    getProductBySlug(ProductSlugs.axeDevToolsMobile);
  const axeAssistantTeams =
    hasAccessToProduct(ProductSlugs.axeAssistantTeams) &&
    getProductBySlug(ProductSlugs.axeAssistantTeams);
  const axeMonitor =
    shouldShowAxeMonitorConfigPage &&
    hasAccessToProduct(ProductSlugs.axeMonitor) &&
    getProductBySlug(ProductSlugs.axeMonitor);

  return (
    <>
      <div
        className={classNames(styles.wrap, commonStyles.resetFieldsets, {
          // This is managed in JS, rather than CSS to help keep in sync with the panel breakpoint
          [styles.collapsed]: narrow
        })}
      >
        <h1 id="configuration-heading">{t('Configuration')}</h1>
        {showNoActiveSubscriptionMessage ? (
          <Trans>
            <div>
              To view Axe Configuration, you must have an active subscription to
              at least 1 product.{' '}
              <Link className="Link" to="/">
                View available products
              </Link>
            </div>
          </Trans>
        ) : (
          <>
            <Trans>
              <p>
                Adjust company-wide settings for your organization. Read about{' '}
                <CauldronLink
                  href="https://docs.deque.com/devtools-server/4.0.0/en/axe-configuration#how-it-works"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  how settings will take effect for each product.
                </CauldronLink>
              </p>
            </Trans>
            <TwoColumnPanel>
              <ColumnLeft aria-labelledby="configuration-sidebar-header">
                <ColumnHeader
                  id="configuration-sidebar-header"
                  className={styles.columnHeader}
                >
                  <h2>{t('Administrator')}</h2>
                </ColumnHeader>
                <nav aria-labelledby="configuration-heading">
                  <ul>
                    <li>
                      <Link
                        to="/configuration"
                        aria-current={paths.globalIsActive}
                      >
                        {t('Global')}
                        <em>
                          {t(
                            'Manage settings that apply to all Deque applications.'
                          )}
                        </em>
                      </Link>
                    </li>
                    {axeDevToolsPro && (
                      <li>
                        <Link
                          to={`/configuration/${ProductSlugs.axeDevToolsExtension}`}
                          aria-current={paths.axeDevToolsProIsActive}
                        >
                          {axeDevToolsPro.name}
                          <em>
                            {t(
                              'Manage settings that apply to {{ axeDevToolsPro }}.',
                              { axeDevToolsPro: axeDevToolsPro.name }
                            )}
                          </em>
                        </Link>
                      </li>
                    )}

                    {axeDeveloperHub && (
                      <li>
                        <Link
                          to={`/configuration/${ProductSlugs.axeDevToolsWatcher}`}
                          aria-current={paths.axeDeveloperHubIsActive}
                        >
                          {axeDeveloperHub.name}
                          <em>
                            {t(
                              'Manage settings that apply to {{ axeDeveloperHub }}.',
                              { axeDeveloperHub: axeDeveloperHub.name }
                            )}
                          </em>
                        </Link>
                      </li>
                    )}

                    {axeDevToolsMobile && (
                      <li>
                        <Link
                          to={`/configuration/${ProductSlugs.axeDevToolsMobile}`}
                          aria-current={paths.axeDevToolsMobileIsActive}
                        >
                          {axeDevToolsMobile.name}
                          <em>
                            {t(
                              'Manage settings that apply to {{ axeDevToolsMobile }}.',
                              { axeDevToolsMobile: axeDevToolsMobile.name }
                            )}
                          </em>
                        </Link>
                      </li>
                    )}

                    {axeMonitor && (
                      <li>
                        <Link
                          to={`/configuration/${ProductSlugs.axeMonitor}`}
                          aria-current={paths.axeMonitorIsActive}
                        >
                          {axeMonitor.name}
                          <em>
                            {t(
                              'Manage settings that apply to {{ axeMonitor }}.',
                              { axeMonitor: axeMonitor.name }
                            )}
                          </em>
                        </Link>
                      </li>
                    )}

                    {canAccessIntegrationConfiguration && (
                      <li>
                        <Link
                          to="/configuration/integrations"
                          aria-current={paths.integrationsIsActive}
                        >
                          {t('Integrations')}
                          <em>
                            {t(
                              'Set up third-party products to work with Axe products.'
                            )}
                          </em>
                        </Link>
                      </li>
                    )}
                    {/* Should only be available to enterprise admins */}
                    {axeAssistantTeams && isEnterpriseAdmin && (
                      <li>
                        <Link
                          to={`/configuration/${ProductSlugs.axeAssistantTeams}`}
                          aria-current={paths.axeAssistantTeamsIsActive}
                        >
                          {axeAssistantTeams.name}
                          <em>
                            {t(
                              'Manage settings that apply to {{ axeAssistantTeams }}.',
                              { axeAssistantTeams: axeAssistantTeams.name }
                            )}
                          </em>
                        </Link>
                      </li>
                    )}
                  </ul>
                </nav>
              </ColumnLeft>
              <ColumnRight aria-labelledby="configuration-item-header">
                <Switch>
                  <Route exact path="/configuration">
                    <GlobalConfiguration />
                  </Route>
                  {axeDevToolsPro && (
                    <Route
                      path={`/configuration/${ProductSlugs.axeDevToolsExtension}`}
                    >
                      <AxeDevtoolsProConfiguration product={axeDevToolsPro} />
                    </Route>
                  )}
                  {axeDeveloperHub && (
                    <Route
                      path={`/configuration/${ProductSlugs.axeDevToolsWatcher}`}
                    >
                      <AxeDeveloperHubConfiguration product={axeDeveloperHub} />
                    </Route>
                  )}
                  {axeDevToolsMobile && (
                    <Route
                      path={`/configuration/${ProductSlugs.axeDevToolsMobile}`}
                    >
                      <AxeDevToolsMobileConfiguration
                        product={axeDevToolsMobile}
                      />
                    </Route>
                  )}
                  {axeAssistantTeams && isEnterpriseAdmin && (
                    <Route
                      path={`/configuration/${ProductSlugs.axeAssistantTeams}`}
                    >
                      <AxeAssistantTeamsConfiguration
                        product={axeAssistantTeams}
                      />
                    </Route>
                  )}
                  {axeMonitor && (
                    <Route path={`/configuration/${ProductSlugs.axeMonitor}`}>
                      <AxeMonitorConfiguration product={axeMonitor} />
                    </Route>
                  )}
                  {canAccessIntegrationConfiguration && activeEnterprise && (
                    <>
                      <Route
                        exact
                        path={`/configuration/integrations/${ProductSlugs.jiraOnPremIntegration}/settings/:id`}
                      >
                        <JiraOnPremSettings
                          activeEnterprise={activeEnterprise}
                        />
                      </Route>
                      <Route
                        exact
                        path={`/configuration/integrations/${ProductSlugs.jiraDirectCloudIntegration}/settings/:id`}
                      >
                        <JiraDirectSettings
                          activeEnterprise={activeEnterprise}
                        />
                      </Route>
                      <Route
                        exact
                        path={`/configuration/integrations/${ProductSlugs.customIntegration}/settings/:id`}
                      >
                        <CustomSettings activeEnterprise={activeEnterprise} />
                      </Route>
                      <Route
                        exact
                        path={`/configuration/integrations/${ProductSlugs.customIntegration}/settings`}
                      >
                        <CustomSettings activeEnterprise={activeEnterprise} />
                      </Route>
                      <Route exact path="/configuration/integrations">
                        <IntegrationConfigurations
                          enabledIntegrationProductSlugs={
                            enabledIntegrationProductSlugs
                          }
                          activeEnterprise={activeEnterprise}
                          isEnterpriseAdmin={isEnterpriseAdmin}
                        />
                      </Route>
                    </>
                  )}
                </Switch>
              </ColumnRight>
            </TwoColumnPanel>
          </>
        )}
      </div>
    </>
  );
};

export default Configuration;
